import { z } from 'zod';
/**
 * Push notifications to the BoardFlight phone app (`docs/push-notifications.md`).
 *
 * One table and one sender for both phones: APNs for an iPhone, Firebase Cloud Messaging for
 * Android. The phone registers its token after sign-in and removes it at sign-out; the Worker
 * pushes to an account's own phones about the account's own installs, and every push carries one
 * deep link under the key {@link PUSH_LINK_KEY}, in the app's strict grammar
 * (`apps/desktop/src-tauri/src/deep_link.rs`).
 */
export const PushProvider = z.enum(['apns', 'fcm']);
/** Which of Apple's two gateways issued an APNs token. A debug build's is `sandbox`. */
export const PushEnvironment = z.enum(['sandbox', 'production']);
export const PushPlatform = z.enum(['ios', 'android']);
/**
 * How many phones one account is pushed to. A registration past it forgets the one seen longest
 * ago rather than being refused: a phone that reinstalls gets a new token, and the old one is the
 * row nobody will miss.
 */
export const MAX_PUSH_DEVICES = 10;
/** The custom key every push carries its `boardflight://` link under. */
export const PUSH_LINK_KEY = 'bf';
/**
 * A token as each provider issues it. An APNs token is hex (32 bytes today, and Apple says not to
 * rely on the length); an FCM registration token is URL-safe text. Both are bounded so a row stays
 * a row, and an APNs token goes into a URL path, so nothing but hex is let through for it.
 */
const ApnsToken = z.string().regex(/^[0-9a-fA-F]{32,512}$/, 'Not an APNs device token.');
const FcmToken = z.string().regex(/^[A-Za-z0-9_:\-.]{32,4096}$/, 'Not an FCM registration token.');
/** A bundle id or an Android package: reverse-DNS, as both platforms spell them. */
const PushAppId = z
    .string()
    .min(3)
    .max(255)
    .regex(/^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)+$/, 'Not a bundle id or package name.');
/**
 * `PUT /v1/me/push-devices` → 204.
 *
 * Idempotent: the same token again stamps it as seen. A token another account holds moves to the
 * caller, because a phone changes hands by signing out and signing in. `environment` is APNs' and
 * is required for it; an FCM token has none, and sends `null`.
 */
export const RegisterPushDeviceRequest = z.discriminatedUnion('provider', [
    z.object({
        provider: z.literal('apns'),
        token: ApnsToken,
        environment: PushEnvironment,
        appId: PushAppId,
        platform: PushPlatform,
    }),
    z.object({
        provider: z.literal('fcm'),
        token: FcmToken,
        environment: z.null(),
        appId: PushAppId,
        platform: PushPlatform,
    }),
]);
/** `DELETE /v1/me/push-devices` → 204, whether or not the caller held that token. */
export const RemovePushDeviceRequest = z.object({
    provider: PushProvider,
    token: z.string().min(1).max(4096),
});
/**
 * What to say about an install that ended, or `null` when there is nothing worth interrupting
 * anybody for: a cancelled job (somebody pressed Cancel, and they know), one that expired unrun,
 * and one still going.
 *
 * The desktop app's own notification, word for word (`words()` in
 * `apps/desktop/src-tauri/src/notify.rs`, which cannot import this). A change to one is a change
 * to both; `packages/shared/test/push.test.ts` holds this side to the sentences the Rust tests
 * hold that side to.
 */
export function jobEndedWords(ended) {
    const game = named(ended.game) ?? 'A game';
    const board = named(ended.board) ?? 'your Board';
    if (ended.status === 'installed') {
        return { title: `${game} is on ${board}`, body: 'Installed and ready to play.' };
    }
    if (ended.status !== 'failed')
        return null;
    if (ended.removeFirst) {
        return {
            title: `${game} was not installed`,
            body: `This version can only go on ${board} if the game is removed first, which deletes its saved data. Open BoardFlight to decide.`,
        };
    }
    const why = named(ended.boardMessage);
    return {
        title: `${game} was not installed on ${board}`,
        body: why ? shortened(why) : 'Open BoardFlight to see what happened.',
    };
}
function named(text) {
    const trimmed = text?.trim() ?? '';
    return trimmed === '' ? null : trimmed;
}
/** A notification is a few lines. Board's refusal can be a page. `shortened` in `notify.rs`. */
function shortened(text) {
    const LIMIT = 180;
    const oneLine = text.split(/\s+/).filter(Boolean).join(' ');
    const chars = Array.from(oneLine);
    return chars.length > LIMIT ? `${chars.slice(0, LIMIT).join('').trimEnd()}…` : oneLine;
}
/**
 * The link a push about a job that ended carries: the game's page, which shows what is on the
 * Board now and, for a build that needs the game removed first, asks. Not `job/<id>`, which the
 * app reads as "run this job" (`deep_link.rs`) and which has nothing left to run.
 */
export function jobEndedLink(appId) {
    return `boardflight://game/${appId}`;
}
//# sourceMappingURL=push.js.map